import { CSSProperties, HTMLAttributes, MouseEventHandler, Ref } from 'react';
import { Header, Table, flexRender, FilterType } from '@tanstack/react-table';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';
import { TableHead, cn } from '@sib/ui';
import { useDataTableContext } from '../../context';
import {
  getTableColumnPinningProperties,
  getTableHeaderFixedSizingStyle,
  getTableHeaderWidth,
} from '../../utils';
import { DataTableHeaderResizeHandler } from './DataTableHeaderResizeHandler';
import {
  resolveFilterProps,
  useGetTableColumnOptions,
} from './DataTableHeader.utils';

interface DataTableHeaderCellProps<T> {
  header: Header<T, unknown>;
  table: Table<T>;
  hideRounding?: boolean;
  withFilters?: boolean;
  withFixedColumnWidth?: boolean;
  isLastInRow?: boolean;
  onSortClick: MouseEventHandler<HTMLTableCellElement>;
  ReorderProps?: {
    ref?: Ref<HTMLTableCellElement>;
    style?: CSSProperties;
    attributes?: HTMLAttributes<HTMLTableCellElement>;
    listeners?: HTMLAttributes<HTMLTableCellElement>;
    active?: boolean;
  };
}

export const DataTableHeaderCell = <T,>({
  header,
  table,
  hideRounding = false,
  withFilters,
  withFixedColumnWidth = false,
  isLastInRow = false,
  onSortClick,
  ReorderProps,
}: DataTableHeaderCellProps<T>) => {
  const getTableColumnOptions = useGetTableColumnOptions();
  const { filters } = useDataTableContext();

  const filter = header.column.columnDef.filter;
  const sorting = header.column.getIsSorted();
  const canUserSort = header.column.columnDef.sortable ?? true;
  const meta = header.column.columnDef.meta;
  const options = getTableColumnOptions<T>(header.column);
  const rawFilterValue = header.column.getFilterValue();

  const arrayVal =
    rawFilterValue === EMPTY_FILTER_VALUE
      ? [EMPTY_FILTER_VALUE]
      : Array.isArray(rawFilterValue)
        ? rawFilterValue
        : rawFilterValue !== undefined && rawFilterValue !== null
          ? [String(rawFilterValue)]
          : [];

  const filterProp = filter && {
    type: filter?.FilterProps?.type as FilterType,
    value: arrayVal as string[],
    onChange: (vals: string[]) => {
      if (vals.length === 0) return header.column.setFilterValue(undefined);
      if (vals.length === 1 && vals[0] === EMPTY_FILTER_VALUE)
        return header.column.setFilterValue(EMPTY_FILTER_VALUE);
      header.column.setFilterValue(vals);
    },
    disabled: filter?.disabled,
  };

  const size = header.column.getSize();
  const className = meta?.className;
  const aplicableClassName =
    typeof className === 'string' ? className : className?.(null as T);

  const { pinPosition, style } = getTableColumnPinningProperties(header.column);

  const widthStyle = withFixedColumnWidth
    ? getTableHeaderFixedSizingStyle(header.id)
    : {
        minWidth: 0,
        width: getTableHeaderWidth(header.id),
      };

  return (
    <TableHead
      ref={ReorderProps?.ref}
      key={header.id}
      canSort={canUserSort}
      size={size}
      style={{
        ...style,
        ...widthStyle,
        ...ReorderProps?.style,
      }}
      tooltip={meta?.headerTooltip}
      data-testid={meta?.dataTestId}
      onClick={onSortClick}
      filter={filterProp}
      clearFilter={() => header.column.setFilterValue(undefined)}
      setFilter={(vals: string[]) => header.column.setFilterValue(vals)}
      filterProps={resolveFilterProps(
        filter?.FilterProps,
        filters[header.column.id]
      )}
      withFilters={withFilters}
      options={options}
      className={cn(
        aplicableClassName,
        'sticky top-0 z-20 overflow-visible whitespace-nowrap',
        pinPosition && 'sticky z-30',
        header.index === 0 && 'bg-primary! text-white',
        hideRounding &&
          (header.index === 0 || isLastInRow) &&
          'rounded-tl-none! rounded-tr-none!',
        isLastInRow && 'bg-primary! text-white',
        ReorderProps?.active && 'z-50 opacity-60'
      )}
      {...(sorting && { sortDirection: sorting })}
      {...ReorderProps?.attributes}
      {...ReorderProps?.listeners}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {header.column.getCanResize() && (
        <DataTableHeaderResizeHandler table={table} header={header} />
      )}
    </TableHead>
  );
};
